import { useState } from "react";
import { LOST_REASON_MIN } from "@salesboard/contracts";
import { DatePicker } from "@/components/ui/DatePicker";
import type { Movable } from "../drag/classifyDrop";
import { Field, Input, Textarea } from "@/components/ui/input";
import { FormDialog } from "@/components/ui/FormDialog";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
// Shared with the detail's inline date field, so the two previews cannot disagree about
// which quarter a date implies. Server-answered, as every derived value is.
import { useT } from "@/i18n";
import { useQuarterPreview } from "@/hooks/useQuarterPreview";

export function ClosureDialog({
  card,
  closedType,
  pending,
  onCancel,
  onConfirm,
}: {
  card: Movable;
  closedType: "won" | "lost";
  pending: boolean;
  onCancel: () => void;
  onConfirm: (actualCloseDate: string, lostReason?: string) => void;
}) {
  const t = useT();
  // Correcting Won → Lost keeps the date already recorded: the outcome changed, not when it
  // happened, and defaulting to today would silently move a July close into August.
  // Otherwise today, which is the ordinary case for recording a close that just happened —
  // not an invented forecast, which is why the *expected* date prompt starts empty.
  const [date, setDate] = useState(() => card.actualCloseDate ?? today());
  /**
   * Why it was lost — asked **here**, in the dialog the closure already required, rather than in
   * a second one afterwards. A lost opportunity must never exist without a reason, so the two
   * facts travel in one request: one version bump, one coherent burst of audit events, and no
   * window in which the record is lost and unexplained.
   */
  const [reason, setReason] = useState("");
  const reasonTooShort = closedType === "lost" && reason.trim().length < LOST_REASON_MIN;
  /**
   * Whether the reader has tried yet.
   *
   * The length rule used to stand under the field as a hint, with the button disabled until it
   * was met. The hint is gone (2026-08-26, asked for), and that forces the trade: a disabled
   * button with nothing explaining it, or an enabled one that says why it refused. The refusal
   * is the better half — nobody reads a rule about a field they have not filled in yet, and
   * everybody reads the line that appears when they press the button.
   */
  const [attempted, setAttempted] = useState(false);

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onCancel()}
      title={closedType === "won" ? t("close.titleWon", card.name) : t("close.titleLost", card.name)}
      submitLabel={closedType === "won" ? t("close.submitWon") : t("close.submitLost")}
      pending={pending}
      // Only a missing date disables it. A too-short reason lets the press through so the
      // refusal can name itself — see `attempted`.
      submitDisabled={!date}
      onSubmit={(event) => {
        event.preventDefault();
        setAttempted(true);
        if (!date || reasonTooShort) return;
        onConfirm(date, closedType === "lost" ? reason.trim() : undefined);
      }}
    >
      <Field label={t("close.actualCloseDate")} htmlFor="actual-close">
        <DatePicker id="actual-close" label={t("close.actualCloseDate")} value={date} onChange={setDate} />
      </Field>

      {closedType === "lost" && (
        <Field
          label={t("opportunity.lostReason")}
          htmlFor="lost-reason"
          error={attempted && reasonTooShort ? t("opportunity.lostReasonTooShort", LOST_REASON_MIN) : undefined}
        >
          <Textarea
            id="lost-reason"
            value={reason}
            rows={3}
            placeholder={t("opportunity.lostReasonPlaceholder")}
            onChange={(event) => setReason(event.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </Field>
      )}

    </FormDialog>
  );
}

/**
 * Reopening: a resumed pursuit is reopened rather than duplicated.
 *
 * Asks for a date only when the drop column differs from the card's current quarter —
 * otherwise the gesture would name a column the reopened card then failed to appear in.
 */
export function ReopenDialog({
  card,
  targetStageName,
  targetQuarter,
  boardYear,
  pending,
  onCancel,
  onConfirm,
}: {
  card: Movable;
  targetStageName: string;
  /** Absent when reopening into the card's own quarter, which needs no date. */
  targetQuarter?: number;
  boardYear: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (expectedCloseDate?: string) => void;
}) {
  const t = useT();
  const [date, setDate] = useState("");
  const preview = useQuarterPreview(date);
  const derived = preview.data;

  const consequence = t("reopen.consequence", targetStageName);

  if (targetQuarter === undefined) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(next) => !next && onCancel()}
        title={t("reopen.title", card.name)}
        consequence={consequence}
        confirmLabel={t("reopen.submit")}
        pending={pending}
        onConfirm={() => onConfirm()}
      />
    );
  }

  const contradictsColumn = derived && derived.year === boardYear && derived.quarter !== targetQuarter;
  const crossesYear = derived && derived.year !== boardYear;

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onCancel()}
      title={t("reopen.title", card.name)}
      description={t("reopen.description", targetStageName, card.quarter, targetQuarter)}
      submitLabel={t("reopen.submit")}
      pending={pending}
      onSubmit={(event) => {
        event.preventDefault();
        if (date) onConfirm(date);
      }}
    >
      <Field label={t("reopen.newDate")} htmlFor="reopen-date" hint={t("move.quarterHint")}>
        <Input
          id="reopen-date"
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </Field>

      <p className="text-muted-foreground text-sm">{consequence}</p>

      {contradictsColumn && (
        <p className="border-warning/40 bg-warning/10 rounded-md border p-2 text-sm">
          {t("move.contradictsColumn", targetQuarter, derived.quarter)}
        </p>
      )}

      {crossesYear && (
        <p className="border-warning/40 bg-warning/10 rounded-md border p-2 text-sm">
          {t("move.crossesYear", derived.year, boardYear)}
        </p>
      )}
    </FormDialog>
  );
}

/**
 * Today, in the viewer's own calendar.
 *
 * `toISOString().slice(0, 10)` is UTC today: between midnight and 02:00 in Prague it names
 * *yesterday*, which for a date that decides which quarter a closure lands in is exactly the
 * class of bug this product writes migrations to avoid.
 */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}
